import React, { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "../ui/card";
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { toast } from "../ui/use-toast";
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { CalendarIcon, PlusCircle, RefreshCw } from 'lucide-react';
import { Calendar } from "../ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { cn } from "../../lib/utils";

type EconomicIndex = {
  id: string;
  reference_date: string;
  index_type: 'IGPM' | 'IPCA';
  index_value: number;
  created_at: string;
  updated_at: string;
};

const EconomicIndicesManager = () => {
  const [indices, setIndices] = useState<EconomicIndex[]>([]);
  const [loading, setLoading] = useState(true);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isAdjustDialogOpen, setIsAdjustDialogOpen] = useState(false);
  const [newIndex, setNewIndex] = useState<Partial<EconomicIndex>>({
    index_type: 'IGPM',
    index_value: 0,
  });
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(new Date());
  const [adjustmentParams, setAdjustmentParams] = useState({
    index_type: 'IGPM',
    start_date: new Date(new Date().getFullYear(), 0, 1), // 1º de Janeiro do ano atual
    end_date: new Date(),
    subscription_id: '',
  });
  const [adjustmentResults, setAdjustmentResults] = useState<any[]>([]);
  const [isAdjusting, setIsAdjusting] = useState(false);

  useEffect(() => {
    fetchIndices();
  }, []);

  const fetchIndices = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('economic_indices')
        .select('*')
        .order('reference_date', { ascending: false })
        .order('index_type', { ascending: true });

      if (error) {
        throw error;
      }

      setIndices(data || []);
    } catch (error: any) {
      console.error('Erro ao buscar índices:', error.message);
      toast({
        title: 'Erro',
        description: `Falha ao buscar índices econômicos: ${error.message}`,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAddIndex = async () => {
    if (!selectedDate) {
      toast({
        title: 'Erro',
        description: 'Por favor, selecione uma data de referência',
        variant: 'destructive',
      });
      return;
    }

    if (newIndex.index_value === undefined || isNaN(Number(newIndex.index_value))) {
      toast({
        title: 'Erro',
        description: 'Por favor, insira um valor de índice válido',
        variant: 'destructive',
      });
      return;
    }

    try {
      const { data, error } = await supabase
        .from('economic_indices')
        .insert({
          reference_date: selectedDate.toISOString().split('T')[0],
          index_type: newIndex.index_type,
          index_value: Number(newIndex.index_value),
        })
        .select();

      if (error) {
        throw error;
      }

      toast({
        title: 'Sucesso',
        description: 'Índice econômico adicionado com sucesso',
      });

      setIsAddDialogOpen(false);
      setNewIndex({
        index_type: 'IGPM',
        index_value: 0,
      });
      setSelectedDate(new Date());
      fetchIndices();
    } catch (error: any) {
      console.error('Erro ao adicionar índice:', error.message);
      toast({
        title: 'Erro',
        description: `Falha ao adicionar índice econômico: ${error.message}`,
        variant: 'destructive',
      });
    }
  };

  const handleAdjustPrices = async () => {
    setIsAdjusting(true);
    try {
      const { data, error } = await supabase.rpc('adjust_subscription_item_prices', {
        p_index_type: adjustmentParams.index_type,
        p_start_date: adjustmentParams.start_date.toISOString().split('T')[0],
        p_end_date: adjustmentParams.end_date.toISOString().split('T')[0],
        p_subscription_id: adjustmentParams.subscription_id || null,
      });

      if (error) {
        throw error;
      }

      setAdjustmentResults(data || []);
      
      toast({
        title: 'Sucesso',
        description: `${data?.length || 0} itens de assinatura foram ajustados com sucesso`,
      });
    } catch (error: any) {
      console.error('Erro ao ajustar preços:', error.message);
      toast({
        title: 'Erro',
        description: `Falha ao ajustar preços: ${error.message}`,
        variant: 'destructive',
      });
    } finally {
      setIsAdjusting(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Gerenciamento de Índices Econômicos</CardTitle>
          <CardDescription>
            Gerencie os índices IGPM e IPCA para ajustes de preços de assinaturas
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex justify-between mb-4">
            <Button onClick={() => setIsAddDialogOpen(true)} className="flex items-center gap-2">
              <PlusCircle className="h-4 w-4" />
              Adicionar Novo Índice
            </Button>
            <Button onClick={() => setIsAdjustDialogOpen(true)} variant="outline" className="flex items-center gap-2">
              <RefreshCw className="h-4 w-4" />
              Ajustar Preços de Assinaturas
            </Button>
          </div>

          <Table>
            <TableCaption>Lista de índices econômicos para ajustes de preços</TableCaption>
            <TableHeader>
              <TableRow>
                <TableHead>Data de Referência</TableHead>
                <TableHead>Tipo de Índice</TableHead>
                <TableHead>Valor (%)</TableHead>
                <TableHead>Criado em</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={4} className="text-center">Carregando...</TableCell>
                </TableRow>
              ) : indices.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} className="text-center">Nenhum índice encontrado</TableCell>
                </TableRow>
              ) : (
                indices.map((index) => (
                  <TableRow key={index.id}>
                    <TableCell>{format(new Date(index.reference_date), 'dd MMM yyyy', { locale: ptBR })}</TableCell>
                    <TableCell>{index.index_type}</TableCell>
                    <TableCell>{(index.index_value * 100).toFixed(4)}%</TableCell>
                    <TableCell>{format(new Date(index.created_at), 'dd MMM yyyy', { locale: ptBR })}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Diálogo para Adicionar Novo Índice */}
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Adicionar Novo Índice Econômico</DialogTitle>
            <DialogDescription>
              Insira os detalhes para o novo índice econômico
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="index-type" className="text-right">
                Tipo de Índice
              </Label>
              <Select
                value={newIndex.index_type}
                onValueChange={(value) => setNewIndex({ ...newIndex, index_type: value as 'IGPM' | 'IPCA' })}
              >
                <SelectTrigger className="col-span-3">
                  <SelectValue placeholder="Selecione o tipo de índice" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="IGPM">IGPM</SelectItem>
                  <SelectItem value="IPCA">IPCA</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="reference-date" className="text-right">
                Data de Referência
              </Label>
              <div className="col-span-3">
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant={"outline"}
                      className={cn(
                        "w-full justify-start text-left font-extrabold",
                        !selectedDate && "text-muted-foreground"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {selectedDate ? format(selectedDate, "PPP", { locale: ptBR }) : <span>Escolha uma data</span>}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={selectedDate}
                      onSelect={setSelectedDate}
                      initialFocus
                      locale={ptBR}
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="index-value" className="text-right">
                Valor (%)
              </Label>
              <Input
                id="index-value"
                type="number"
                step="0.0001"
                value={newIndex.index_value !== undefined ? newIndex.index_value * 100 : ''}
                onChange={(e) => setNewIndex({ ...newIndex, index_value: Number(e.target.value) / 100 })}
                className="col-span-3"
                placeholder="Insira o valor do índice em percentual"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAddDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleAddIndex}>Adicionar Índice</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Diálogo para Ajustar Preços */}
      <Dialog open={isAdjustDialogOpen} onOpenChange={setIsAdjustDialogOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Ajustar Preços de Assinaturas</DialogTitle>
            <DialogDescription>
              Ajuste os preços das assinaturas com base nos índices econômicos
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="adjustment-index-type" className="text-right">
                Tipo de Índice
              </Label>
              <Select
                value={adjustmentParams.index_type}
                onValueChange={(value) => setAdjustmentParams({ ...adjustmentParams, index_type: value as 'IGPM' | 'IPCA' })}
              >
                <SelectTrigger className="col-span-3">
                  <SelectValue placeholder="Selecione o tipo de índice" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="IGPM">IGPM</SelectItem>
                  <SelectItem value="IPCA">IPCA</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="start-date" className="text-right">
                Data Inicial
              </Label>
              <div className="col-span-3">
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant={"outline"}
                      className={cn(
                        "w-full justify-start text-left font-extrabold",
                        !adjustmentParams.start_date && "text-muted-foreground"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {adjustmentParams.start_date ? format(adjustmentParams.start_date, "PPP", { locale: ptBR }) : <span>Escolha uma data</span>}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={adjustmentParams.start_date}
                      onSelect={(date) => setAdjustmentParams({ ...adjustmentParams, start_date: date || new Date() })}
                      initialFocus
                      locale={ptBR}
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="end-date" className="text-right">
                Data Final
              </Label>
              <div className="col-span-3">
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant={"outline"}
                      className={cn(
                        "w-full justify-start text-left font-extrabold",
                        !adjustmentParams.end_date && "text-muted-foreground"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {adjustmentParams.end_date ? format(adjustmentParams.end_date, "PPP", { locale: ptBR }) : <span>Escolha uma data</span>}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={adjustmentParams.end_date}
                      onSelect={(date) => setAdjustmentParams({ ...adjustmentParams, end_date: date || new Date() })}
                      initialFocus
                      locale={ptBR}
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="subscription-id" className="text-right">
                ID da Assinatura
              </Label>
              <Input
                id="subscription-id"
                value={adjustmentParams.subscription_id}
                onChange={(e) => setAdjustmentParams({ ...adjustmentParams, subscription_id: e.target.value })}
                className="col-span-3"
                placeholder="Opcional - Deixe em branco para ajustar todas as assinaturas ativas"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAdjustDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleAdjustPrices} disabled={isAdjusting}>
              {isAdjusting ? 'Ajustando...' : 'Ajustar Preços'}
            </Button>
          </DialogFooter>

          {adjustmentResults.length > 0 && (
            <div className="mt-4">
              <h3 className="text-lg font-extrabold">Resultados do Ajuste</h3>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Item de Assinatura</TableHead>
                    <TableHead>Preço Antigo</TableHead>
                    <TableHead>Preço Novo</TableHead>
                    <TableHead>Fator de Ajuste</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {adjustmentResults.map((result, index) => (
                    <TableRow key={index}>
                      <TableCell>{result.subscription_item_id}</TableCell>
                      <TableCell>{result.old_price}</TableCell>
                      <TableCell>{result.new_price}</TableCell>
                      <TableCell>{(result.adjustment_factor * 100 - 100).toFixed(2)}%</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EconomicIndicesManager; 